import React from 'react'
import {
  List,
  useTable,
  EditButton,
  ShowButton,
  DeleteButton,
  DateField,
} from '@refinedev/antd'
import { useList, useUpdate } from '@refinedev/core'
import { Link } from 'react-router'
import { Table, Space, Switch, Tooltip } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { ListPage, TableSurface, Pill, useRowShow, type Stat } from '../../theme/listkit'
import { CalendarDays, CalendarCheck } from '../../components/icons'

type Event = Tables<'events'>
type EventSession = Tables<'event_sessions'>

const fmtDate = (value: string): string =>
  new Date(value + (value.length <= 10 ? 'T00:00:00' : '')).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

export function EventList() {
  // LIVE opt-in (the global liveMode stays 'off' — see App.tsx): the events
  // table subscribes with liveMode:'auto' so a create/edit/hero-pin made in
  // another tab or by another admin lands here without a manual refresh. One
  // bounded page of rows per refetch — cheap, unlike the blanket liveMode we
  // removed globally.
  const { tableProps } = useTable<Event>({
    syncWithLocation: true,
    liveMode: 'auto',
  })
  // Whole-row click → the event's Show page (ignores clicks on the row's own
  // buttons / featured Switch — see useRowShow).
  const onRow = useRowShow('events')
  // Home-hero pin: flipping one ON clears any other via the DB trigger
  // (trg_events_single_hero); the useUpdate invalidation refetches the list so
  // the previously-pinned row visibly unpins.
  const { mutate: updateEvent } = useUpdate<Event>()

  // Summary deck — `events` has no aggregate view, so each number is a BOUNDED
  // count query (pageSize:1; PostgREST returns the total without the rows).
  // The cutoff is computed once per render so all three counts agree.
  const nowIso = React.useMemo(() => new Date().toISOString().slice(0, 10), [])

  const { result: totalRes } = useList<Event>({
    resource: 'events',
    pagination: { pageSize: 1 },
  })
  const { result: upcomingRes } = useList<Event>({
    resource: 'events',
    filters: [{ field: 'start_date', operator: 'gte', value: nowIso }],
    pagination: { pageSize: 1 },
  })
  const { result: pastRes } = useList<Event>({
    resource: 'events',
    filters: [{ field: 'start_date', operator: 'lt', value: nowIso }],
    pagination: { pageSize: 1 },
  })
  // The single next-up event: earliest upcoming start_date.
  const { result: nextRes } = useList<Event>({
    resource: 'events',
    filters: [{ field: 'start_date', operator: 'gte', value: nowIso }],
    sorters: [{ field: 'start_date', order: 'asc' }],
    pagination: { pageSize: 1 },
  })

  // Per-event sub-event counts, derived client-side from ONE bounded fetch of
  // just (id, event_id). 200 is a documented cap — the whole calendar has a few
  // dozen sessions today; if it ever exceeds 200 the counts under-report and
  // this should move to a grouped view. Cached 5min between changes, and LIVE
  // via liveMode:'auto' so adding/deleting a sub-event elsewhere updates the
  // counts column here — a different resource (event_sessions) from the
  // table's `events` subscription above, so the page stays at one live hook
  // per resource and the refetch is this same bounded two-column read.
  const { result: sessionsRes } = useList<EventSession>({
    resource: 'event_sessions',
    pagination: { pageSize: 200 },
    meta: { select: 'id,event_id' },
    liveMode: 'auto',
    queryOptions: { staleTime: 300_000 },
  })
  const sessionCounts = React.useMemo<Record<string, number>>(() => {
    const counts: Record<string, number> = {}
    for (const s of (sessionsRes?.data ?? []) as EventSession[]) {
      if (s.event_id) counts[s.event_id] = (counts[s.event_id] ?? 0) + 1
    }
    return counts
  }, [sessionsRes])

  const now = Date.now()
  const totalCount = totalRes?.total ?? 0
  const upcomingCount = upcomingRes?.total ?? 0
  const pastCount = pastRes?.total ?? 0
  const nextUp = nextRes?.data?.[0]

  const stats: Stat[] = [
    { label: 'Total Events', value: totalCount.toLocaleString(), icon: CalendarDays, tone: 'gold' },
    { label: 'Upcoming', value: upcomingCount.toLocaleString(), icon: CalendarCheck, tone: 'jaguar' },
    { label: 'Past', value: pastCount.toLocaleString(), icon: CalendarDays, tone: 'neutral' },
    {
      label: 'Next',
      value: nextUp ? '1' : '—',
      hint: nextUp ? fmtDate(nextUp.start_date as string) : undefined,
      icon: CalendarCheck,
      tone: 'green',
    },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Events',
        description:
          'Every gathering on the alumni calendar — homecomings, galas, reunions and Sabbath services. Schedule new events, keep dates current, and see at a glance what is coming next.',
      }}
      stats={stats}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Event>
              dataIndex="title"
              title="Title"
              render={(title: string) => (
                <span style={{ fontFamily: brand.fDisplay, fontWeight: 700, color: brand.ink }}>{title}</span>
              )}
            />
            <Table.Column<Event>
              dataIndex="location"
              title="Location"
              render={(loc: string | null) =>
                loc ? (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.sub }}>{loc}</span>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<Event>
              dataIndex="start_date"
              title="Start Date"
              render={(value: string | null) =>
                value ? <DateField value={value} format="MMM D, YYYY" /> : '—'
              }
            />
            <Table.Column<Event>
              dataIndex="end_date"
              title="End Date"
              render={(value: string | null) =>
                value ? <DateField value={value} format="MMM D, YYYY" /> : '—'
              }
            />
            <Table.Column<Event>
              title="Window"
              dataIndex="start_date"
              key="window"
              render={(value: string | null) =>
                value && new Date(value).getTime() >= now ? (
                  <Pill tone="jaguar">Upcoming</Pill>
                ) : value ? (
                  <Pill tone="neutral">Past</Pill>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<Event>
              title="Sub-events"
              key="sub_events"
              render={(_: unknown, record: Event) => {
                const count = sessionCounts[record.id] ?? 0
                // The href is refine's syncWithLocation filter querystring — the
                // sub-events list (syncWithLocation: true) parses it and lands
                // pre-filtered to this event's children.
                const href =
                  `/event_sessions?filters[0][field]=event_id` +
                  `&filters[0][operator]=eq&filters[0][value]=${record.id}`
                return (
                  <Link
                    to={href}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      fontFamily: brand.fSans,
                      fontWeight: 700,
                      color: count > 0 ? brand.gold : brand.sub,
                    }}
                  >
                    {count}
                  </Link>
                )
              }}
            />
            <Table.Column<Event>
              title="Featured"
              dataIndex="is_featured"
              key="is_featured"
              render={(v: boolean | null) =>
                v ? <Pill tone="gold">Featured</Pill> : <span style={{ color: brand.sub }}>—</span>
              }
            />
            <Table.Column<Event>
              title="Home Hero"
              dataIndex="hero_featured"
              key="hero_featured"
              render={(v: boolean, record: Event) => (
                <Tooltip
                  title={
                    v
                      ? 'Pinned as the Home hero — switch off to fall back to the latest event'
                      : 'Pin this event into the mobile Home hero (unpins any other event)'
                  }
                >
                  <Switch
                    checked={Boolean(v)}
                    size="small"
                    onChange={(checked) =>
                      updateEvent({
                        resource: 'events',
                        id: record.id,
                        values: { hero_featured: checked },
                        successNotification: {
                          type: 'success',
                          message: checked
                            ? `“${record.title}” is now the Home hero`
                            : 'Home hero unpinned — the latest event shows by default',
                        },
                      })
                    }
                  />
                </Tooltip>
              )}
            />
            <Table.Column<Event>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Event) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
